import { useEffect, useState } from 'react'

import { useAuth } from './AuthProvider.tsx'

// Reads the signed-in user's **sandbox** API key so the docs playground is
// prefilled and a visitor can run a request the moment they land.
//
// Sandbox is the whole safety argument: `findByExternalIdAndApiKey` sets
// `isSandbox` when the match is the sandbox key, so every try-it request is
// sandbox-scoped server-side even though this is a real credential.
//
// From curri-api because it owns the key state, authorized as a self-read.
// `userApiKeys` hands a credential caller the sandbox key only and will not mint
// keys for one, so an account with no keys yet simply prefills nothing.

export interface BasicCredentials {
  /** The user's externalId — the username half of `base64(externalUserId:apiKey)`. */
  username: string
  password: string
}

// `currentUser` supplies the externalId because `GET /api/me` deliberately
// returns the numeric id only. Both halves in one round trip.
const QUERY = `query PortalSandboxCredentials {
  currentUser { externalId }
  userApiKeys { sandboxKey }
}`

interface CredentialsResponse {
  data?: {
    currentUser?: { externalId?: string | null } | null
    userApiKeys?: { sandboxKey?: string | null } | null
  }
  errors?: { message?: string; path?: unknown[] }[]
}

// Prefilling is a convenience and must never break the reference — but failing
// silently is worse. GraphQL answers 200 with an `errors` array, so `response.ok`
// alone turned a real failure into an empty form with no diagnostic anywhere.
const warn = (reason: string, detail?: unknown) => {
  // eslint-disable-next-line no-console
  console.warn(`[portal] sandbox credential prefill skipped: ${reason}`, detail ?? '')
}

export const useSandboxCredentials = (): BasicCredentials | null => {
  const { state, config, getAccessToken } = useAuth()
  const [credentials, setCredentials] = useState<BasicCredentials | null>(null)

  const authenticated = state.status === 'authenticated'

  useEffect(() => {
    if (!authenticated) {
      // Drop the credential on sign-out so it cannot outlive the session.
      setCredentials(null)
      return
    }

    let cancelled = false

    void (async () => {
      try {
        const response = await fetch(config.graphqlUrl, {
          method: 'POST',
          // Deliberately NOT `Curri-Version`: it is the integrations REST API's
          // version gate and means nothing to curri-api. Sending it would put a
          // header on the CORS preflight that curri-api's gateway policy does not
          // allow (`allowHeaders`, infra/k8/apps/curri-api/security-policy.yml),
          // so the prefill would work locally — where in-process `cors()` reflects
          // back whatever headers are asked for — and silently fail in prod, where
          // the gateway's explicit list governs. Both headers below are on it.
          headers: {
            'content-type': 'application/json',
            Authorization: `Bearer ${getAccessToken() ?? ''}`,
          },
          body: JSON.stringify({
            query: QUERY,
            operationName: 'PortalSandboxCredentials',
          }),
        })
        if (!response.ok) {
          warn(`credential request returned HTTP ${response.status}`)
          return
        }

        const body = (await response.json()) as CredentialsResponse
        if (body.errors?.length) {
          warn('GraphQL returned errors', body.errors.map(e => e.message))
          return
        }

        const username = body.data?.currentUser?.externalId
        const password = body.data?.userApiKeys?.sandboxKey

        if (cancelled) return
        if (!username || !password) {
          // Half a credential prefills a form that 401s confusingly.
          warn(
            !username
              ? 'no externalId on currentUser'
              : 'no sandboxKey for this account'
          )
          return
        }

        setCredentials({ username, password })
      } catch (error) {
        warn('credential request failed', error)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [authenticated, config.graphqlUrl, getAccessToken])

  return credentials
}
